import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { jemAPI, sitesAPI, assetsAPI } from '../utils/api';
import {
  Plug, RefreshCw, Play, Square, Link, Unlink, Zap, Battery,
  CheckCircle, XCircle, AlertTriangle, Clock, Wifi, WifiOff, Plus, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const POLL_STATUS_ICON = {
  success: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
};

export default function JEMIntegrationPage() {
  const [activeTab, setActiveTab] = useState('stations');
  const [authStatus, setAuthStatus] = useState(null);
  const [stations, setStations] = useState([]);
  const [systems, setSystems] = useState({});
  const [mappings, setMappings] = useState([]);
  const [pollStatuses, setPollStatuses] = useState([]);
  const [pollingStatus, setPollingStatus] = useState({ active: false, poll_interval_minutes: 5 });
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [authRes, stationsRes, mappingsRes, pollRes, pollingRes, sitesRes, assetsRes] = await Promise.all([
        jemAPI.authStatus(),
        jemAPI.getStations(),
        jemAPI.getMappings(),
        jemAPI.getPollStatuses(),
        jemAPI.pollingStatus(),
        sitesAPI.getAll(),
        assetsAPI.getAll(),
      ]);
      setAuthStatus(authRes.data);
      setStations(stationsRes.data);
      setMappings(mappingsRes.data);
      setPollStatuses(pollRes.data);
      setPollingStatus(pollingRes.data);
      setSites(sitesRes.data);
      setAssets(assetsRes.data);
    } catch { toast.error('Failed to load JEM data'); }
    finally { setLoading(false); }
  };

  const handleSetToken = async () => {
    if (!tokenInput.trim()) return;
    try {
      await jemAPI.setToken(tokenInput.trim(), 8);
      toast.success('Token set');
      setTokenInput('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleSyncStations = async () => {
    setSyncing(true);
    try {
      const res = await jemAPI.syncStations();
      toast.success(`Synced ${res.data.count} stations`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed — check auth token'); }
    finally { setSyncing(false); }
  };

  const handleSyncSystems = async (stationId) => {
    try {
      const res = await jemAPI.syncSystems(stationId);
      toast.success(`Synced ${res.data.count} systems`);
      setSystems(prev => ({ ...prev, [stationId]: res.data.systems }));
      // Also refresh from cache
      const cached = await jemAPI.getSystems(stationId);
      setSystems(prev => ({ ...prev, [stationId]: cached.data }));
    } catch (e) { toast.error(e.response?.data?.detail || 'System sync failed'); }
  };

  const handleCreateMapping = async (systemData, siteId, assetId) => {
    try {
      await jemAPI.createMapping({
        station_id: systemData.station_id,
        system_id: systemData.system_id,
        ems_device_id: systemData.ems_device_id,
        site_id: siteId,
        asset_id: assetId,
        site_name: sites.find(s => s.site_id === siteId)?.name || '',
      });
      toast.success('Mapping created');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCreateAssetFromSystem = async (systemData, siteId) => {
    try {
      await jemAPI.createAssetFromSystem({
        site_id: siteId,
        station_id: systemData.station_id,
        system_id: systemData.system_id,
        ems_device_id: systemData.ems_device_id,
        ems_sn: systemData.ems_sn,
        rated_power: systemData.rated_power,
        nominal_energy_cap: systemData.nominal_energy_cap,
        name: systemData.name || `JEM ${systemData.ems_device_id}`,
      });
      toast.success('Asset created and mapped');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Remove this mapping?')) return;
    try {
      await jemAPI.deleteMapping(mappingId);
      toast.success('Mapping removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handlePollAsset = async (assetId) => {
    try {
      const res = await jemAPI.pollAsset(assetId);
      if (res.data.status === 'success') toast.success('Poll successful');
      else toast.warning(res.data.message || 'Poll returned no data');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Poll failed'); }
  };

  const handleTogglePolling = async () => {
    try {
      if (pollingStatus.active) {
        await jemAPI.stopPolling();
        toast.success('Polling stopped');
      } else {
        await jemAPI.startPolling();
        toast.success('Polling started');
      }
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (loading) {
    return (<div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>);
  }

  const tabs = [
    { key: 'stations', label: 'Stations & Systems' },
    { key: 'mappings', label: `Mappings (${mappings.length})` },
    { key: 'polling', label: 'Polling & Status' },
  ];

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="jem-integration-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <PageHeader title="JEM iESSCloud" description="Vendor Integration — Station Discovery, Asset Mapping, Telemetry Polling" />

          {/* Auth Status Bar */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-5 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-3 h-3 rounded-full ${authStatus?.has_token ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-sm text-slate-700">
                {authStatus?.has_token ? `Authenticated (expires ${authStatus.expires_at ? new Date(authStatus.expires_at).toLocaleTimeString() : 'N/A'})` : 'Not authenticated'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Input placeholder="Paste x-auth token..." value={tokenInput} onChange={e => setTokenInput(e.target.value)} className="w-64 text-xs" data-testid="jem-token-input" />
              <Button size="sm" onClick={handleSetToken} disabled={!tokenInput} data-testid="set-token-btn" className="bg-slate-900">Set Token</Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-5">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`jem-tab-${t.key}`}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'stations' && (
            <StationsTab
              stations={stations} systems={systems} sites={sites} assets={assets} mappings={mappings}
              onSyncStations={handleSyncStations} onSyncSystems={handleSyncSystems}
              onCreateMapping={handleCreateMapping} onCreateAsset={handleCreateAssetFromSystem}
              syncing={syncing} selectedStation={selectedStation} setSelectedStation={setSelectedStation}
            />
          )}
          {activeTab === 'mappings' && (
            <MappingsTab mappings={mappings} pollStatuses={pollStatuses} onDelete={handleDeleteMapping} onPoll={handlePollAsset} />
          )}
          {activeTab === 'polling' && (
            <PollingTab pollingStatus={pollingStatus} pollStatuses={pollStatuses} mappings={mappings} onToggle={handleTogglePolling} onPoll={handlePollAsset} />
          )}
        </div>
      </div>
    </div>
  );
}

function StationsTab({ stations, systems, sites, assets, mappings, onSyncStations, onSyncSystems, onCreateMapping, onCreateAsset, syncing, selectedStation, setSelectedStation }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-slate-900">JEM Stations</h3>
        <Button onClick={onSyncStations} disabled={syncing} className="bg-slate-900" data-testid="sync-stations-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync Stations'}
        </Button>
      </div>

      {stations.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Plug className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No stations synced. Set your auth token and click "Sync Stations".</p>
        </div>
      ) : (
        <div className="space-y-3">
          {stations.map(station => {
            const stId = station.station_id;
            const stSystems = systems[stId] || [];
            const isOpen = selectedStation === stId;
            return (
              <div key={stId} className="bg-white border border-slate-200 rounded-sm" data-testid={`station-${stId}`}>
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setSelectedStation(isOpen ? null : stId)}>
                  <div>
                    <p className="font-semibold text-slate-900">{station.station_name}</p>
                    <p className="text-xs text-slate-500">ID: {stId} &middot; TZ: {station.timezone || station.offset}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onSyncSystems(stId); }} data-testid={`sync-systems-${stId}`}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Sync Systems
                    </Button>
                  </div>
                </div>

                {isOpen && stSystems.length > 0 && (
                  <div className="border-t border-slate-100 p-4">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Systems ({stSystems.length})</p>
                    <div className="space-y-2">
                      {stSystems.map(sys => {
                        const sysId = sys.system_id || sys.id;
                        const alreadyMapped = mappings.some(m => m.system_id === sysId);
                        return (
                          <SystemMappingRow
                            key={sysId}
                            system={{ ...sys, station_id: stId, system_id: sysId }}
                            sites={sites} assets={assets}
                            alreadyMapped={alreadyMapped}
                            onCreateMapping={onCreateMapping}
                            onCreateAsset={onCreateAsset}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                {isOpen && stSystems.length === 0 && (
                  <div className="border-t border-slate-100 p-4 text-center">
                    <p className="text-xs text-slate-400">No systems found. Click "Sync Systems" above.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SystemMappingRow({ system, sites, assets, alreadyMapped, onCreateMapping, onCreateAsset }) {
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedAsset, setSelectedAsset] = useState('');
  const siteAssets = selectedSite ? assets.filter(a => a.site_id === selectedSite) : [];

  return (
    <div className={`p-3 rounded border ${alreadyMapped ? 'border-green-200 bg-green-50' : 'border-slate-100'}`} data-testid={`system-row-${system.system_id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium">{system.name || system.ems_device_id || `System ${system.system_id}`}</p>
          <p className="text-xs text-slate-500">
            ID: {system.system_id} | Device: {system.ems_device_id || 'N/A'}
            {system.rated_power ? ` | ${system.rated_power}kW` : ''}
            {system.nominal_energy_cap ? ` / ${system.nominal_energy_cap}kWh` : ''}
          </p>
        </div>
        {alreadyMapped && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Mapped</span>}
      </div>

      {!alreadyMapped && (
        <div className="mt-2 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px]">Site</Label>
            <select value={selectedSite} onChange={e => { setSelectedSite(e.target.value); setSelectedAsset(''); }}
              className="w-full mt-0.5 px-2 py-1.5 border border-slate-200 rounded text-xs">
              <option value="">Select site...</option>
              {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex-1">
            <Label className="text-[10px]">Asset</Label>
            <select value={selectedAsset} onChange={e => setSelectedAsset(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 border border-slate-200 rounded text-xs">
              <option value="">Select asset...</option>
              {siteAssets.map(a => <option key={a.asset_id} value={a.asset_id}>{a.asset_name} ({a.installed_power_kw}kW)</option>)}
            </select>
          </div>
          <Button size="sm" disabled={!selectedSite || !selectedAsset}
            onClick={() => onCreateMapping(system, selectedSite, selectedAsset)}
            className="text-xs h-7 bg-slate-900" data-testid={`link-system-${system.system_id}`}>
            <Link className="w-3 h-3 mr-1" /> Link
          </Button>
          <Button size="sm" variant="outline" disabled={!selectedSite}
            onClick={() => onCreateAsset(system, selectedSite)}
            className="text-xs h-7" data-testid={`create-from-system-${system.system_id}`}>
            <Plus className="w-3 h-3 mr-1" /> New Asset
          </Button>
        </div>
      )}
    </div>
  );
}

function MappingsTab({ mappings, pollStatuses, onDelete, onPoll }) {
  const pollMap = pollStatuses.reduce((m, p) => { m[p.asset_id] = p; return m; }, {});

  return (
    <div>
      <h3 className="font-semibold text-slate-900 mb-4">Asset Mappings</h3>
      {mappings.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-sm">
          <Link className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No mappings configured. Go to Stations tab to link systems.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="mappings-table">
            <thead><tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase">
              <th className="px-4 py-3 text-left">Site / Asset</th>
              <th className="px-4 py-3 text-left">Station / System</th>
              <th className="px-4 py-3 text-left">EMS Device</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Last Poll</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-50">
              {mappings.map(m => {
                const ps = pollMap[m.asset_id];
                const stCfg = POLL_STATUS_ICON[ps?.last_poll_status] || POLL_STATUS_ICON.pending;
                const StIcon = stCfg.icon;
                return (
                  <tr key={m.mapping_id} data-testid={`mapping-row-${m.mapping_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{m.site_name || m.site_id}</p>
                      <p className="text-xs text-slate-500">{m.asset_id?.slice(0, 8)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      Stn {m.station_id} / Sys {m.system_id}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{m.ems_device_id || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded ${stCfg.bg} ${stCfg.color}`}>
                        <StIcon className="w-3 h-3 mr-1" />{ps?.last_poll_status || 'pending'}
                      </span>
                      {ps?.last_error && <p className="text-[10px] text-red-500 mt-0.5 max-w-[200px] truncate">{ps.last_error}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {ps?.last_poll_ts ? new Date(ps.last_poll_ts).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onPoll(m.asset_id)} data-testid={`sync-now-${m.mapping_id}`}>
                        <Zap className="w-3 h-3 mr-1" /> Sync Now
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs h-7 text-red-500 hover:text-red-700" onClick={() => onDelete(m.mapping_id)} data-testid={`delete-mapping-${m.mapping_id}`}>
                        <Unlink className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PollingTab({ pollingStatus, pollStatuses, mappings, onToggle, onPoll }) {
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900">Background Polling</h3>
            <p className="text-xs text-slate-500">Auto-polls all enabled mappings at configured interval</p>
          </div>
          <Button onClick={onToggle} className={pollingStatus.active ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="toggle-polling-btn">
            {pollingStatus.active ? <><Square className="w-4 h-4 mr-2" /> Stop Polling</> : <><Play className="w-4 h-4 mr-2" /> Start Polling</>}
          </Button>
        </div>
        <div className="flex items-center space-x-6 text-sm">
          <div>
            <span className="text-slate-500">Status:</span>
            <span className={`ml-2 font-semibold ${pollingStatus.active ? 'text-green-600' : 'text-slate-400'}`}>
              {pollingStatus.active ? 'Running' : 'Stopped'}
            </span>
          </div>
          <div>
            <span className="text-slate-500">Interval:</span>
            <span className="ml-2 font-semibold">{pollingStatus.poll_interval_minutes} min</span>
          </div>
          <div>
            <span className="text-slate-500">Active Mappings:</span>
            <span className="ml-2 font-semibold">{mappings.filter(m => m.enabled).length}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Last Poll Results</h3>
        {pollStatuses.length === 0 ? (
          <p className="text-sm text-slate-500">No poll data yet.</p>
        ) : (
          <div className="space-y-2">
            {pollStatuses.map(ps => {
              const m = mappings.find(mm => mm.asset_id === ps.asset_id);
              const stCfg = POLL_STATUS_ICON[ps.last_poll_status] || POLL_STATUS_ICON.pending;
              const StIcon = stCfg.icon;
              return (
                <div key={ps.asset_id} className={`p-3 rounded border flex items-center justify-between ${stCfg.bg} border-slate-100`}>
                  <div className="flex items-center space-x-3">
                    <StIcon className={`w-4 h-4 ${stCfg.color}`} />
                    <div>
                      <p className="text-sm font-medium">{m?.site_name || ps.asset_id}</p>
                      <p className="text-[10px] text-slate-500">{ps.last_poll_ts ? new Date(ps.last_poll_ts).toLocaleString() : '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {ps.last_error && <p className="text-[10px] text-red-500 max-w-[200px] truncate">{ps.last_error}</p>}
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onPoll(ps.asset_id)}>
                      <Zap className="w-3 h-3 mr-1" /> Sync
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
